import { FeatureImportanceSummary } from "../types";
import { FALLBACK_COLOR } from "./constants";
import { EChartsTransformer } from "./transformer";
import { formatAxisLabel, stripCodeSuffix } from "./utils/labelFormatter";

export const decisionPlotTransformer: EChartsTransformer<FeatureImportanceSummary> = {
  transform: (dpData, wellData, chartContext, _, target) => {
    const isFullscreen = chartContext?.isFullscreen ?? false;
    const targetUnitLabel = target?.Unit ? ` (${target.Unit})` : "";

    if (!dpData) {
      return {};
    }

    const { base_value, features, entities, shap_values } = dpData.decision_plot;
    const yLabels = ["Base Value", ...features.map(stripCodeSuffix)];
    return {
      animation: false,
      tooltip: {
        trigger: "axis",
        confine: true,
        enterable: true,
        extraCssText: "border-style:none;",
        className: "chart-tooltip-container",
        showDelay: 200,
        hideDelay: 0,
        show: true,
        axisPointer: {
          label: {
            precision: 2,
            show: true,
            formatter: function (params) {
              return String(params.value);
            }
          },
          type: "cross",
          snap: true
        },
        formatter: function (params) {
          if (!Array.isArray(params) || params.length === 0 || !chartContext.hoveredId)
            return "";
          const closestSeries = params.find((s) => {
            return s.seriesName == chartContext.hoveredId;
          });
          if (!closestSeries) {
            return "";
          }
          // Create tooltip with just the closest item
          const marker = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${closestSeries.color};"></span>`;

          return `<div style="font-weight:bold">${closestSeries.seriesName}</div>

                       <div>${marker}${String(closestSeries.axisValueLabel)}</div>`;
        }
      },
      dataZoom: [
        {
          type: "inside",
          xAxisIndex: 0
        },
        {
          type: "inside",
          yAxisIndex: 0
        }
      ],
      legend: {
        show: false
      },
      grid: {
        top: 0,
        left: isFullscreen ? 153 : 113,
        right: 20,
        bottom: 45,
        containLabel: false
      },
      xAxis: {
        type: "value",
        name: `Prediction Value${targetUnitLabel}`,
        nameLocation: "middle",
        nameGap: 30
      },
      yAxis: {
        type: "category",
        data: yLabels,
        nameGap: isFullscreen ? 160 : 120,
        axisLabel: {
          interval: 0,
          fontSize: isFullscreen ? 12 : 10,
          rotate: 0,
          width: isFullscreen ? 145 : 105,
          overflow: "truncate",
          lineHeight: isFullscreen ? 12 : 9.5,
          formatter: function (value) {
            return formatAxisLabel({
              value,
              dataLength: yLabels.length,
              isFullscreen
            });
          }
        }
      },
      series: entities.map((entity, entityIdx) => {
        const shap = shap_values[entityIdx];
        const cumulative: number[] = [base_value];
        if (Array.isArray(shap)) {
          for (let i = 0; i < shap.length; i++) {
            cumulative.push(cumulative[i] + shap[i]);
          }
        }
        // Reverse the data to match the reversed yLabels
        const color = wellData?.[entity]?.color || FALLBACK_COLOR;
        return {
          name: entity,
          type: "line",
          data: cumulative,
          showSymbol: false,
          color,
          animation: false,
          lineStyle: {
            width: 1,
            opacity: 0.7,
            color
          },
          tooltip: {
            show: true
          }
        };
      })
    };
  }
};
